/* Supabase Auth with an email magic link (PKCE). A 6-digit code from the same email is accepted too. */
import { GoTrueClient, isAuthRetryableFetchError, type AuthChangeEvent, type Session } from "@supabase/auth-js";
import { RepoError } from "../storage/errors";
import type { CloudConfig } from "./config";

export const AUTH_STORAGE_KEY = "memolink-auth";
/** set by the tab that logs out, so other open tabs clear the screen too (an expired session does not set it) */
const LOGOUT_MARK = "memolink-logout-at";

/**
 * Auth requests give up after this long. Supabase Auth runs one request at a time, so a request that never
 * answers would otherwise also hold up the next try and every session check after it.
 */
export const AUTH_FETCH_TIMEOUT_MS = 18_000;
const fetchWithTimeout: typeof fetch = (input, init) => {
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), AUTH_FETCH_TIMEOUT_MS);
  init?.signal?.addEventListener("abort", () => ctl.abort(), { once: true });
  return fetch(input, { ...init, signal: ctl.signal }).finally(() => clearTimeout(timer));
};

/** only the Auth part of supabase-js; data goes through CloudRepository's own REST calls */
let client: { auth: GoTrueClient } | null = null;
let authCfg: CloudConfig | null = null;
export function authClient(cfg?: CloudConfig): { auth: GoTrueClient } {
  if (!client) {
    if (!cfg) throw new Error("auth not configured");
    authCfg = cfg;
    client = { auth: new GoTrueClient({
      url: `${cfg.url}/auth/v1`,
      headers: { apikey: cfg.anonKey, Authorization: `Bearer ${cfg.anonKey}` },
      fetch: fetchWithTimeout, flowType: "pkce", persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, storageKey: AUTH_STORAGE_KEY
    }) };
  }
  return client;
}

/** where the magic link sends the person back: this page, without query or hash */
export const redirectUrl = () => location.origin + location.pathname;

const linkErrorText = (desc: string) =>
  /provider is not enabled|unsupported provider/i.test(desc) ? "ยังเข้าสู่ระบบด้วยบัญชีนี้ไม่ได้ ใช้อีเมลแทนได้"
  : /expired|invalid/i.test(desc) ? "ลิงก์เข้าสู่ระบบหมดอายุหรือถูกใช้ไปแล้ว ขอลิงก์ใหม่ได้เลย"
  : /code verifier|both auth code and code verifier/i.test(desc) ? "ลิงก์นี้ต้องเปิดในเบราว์เซอร์เดียวกับที่ขอลิงก์ หรือใช้รหัส 6 หลักจากอีเมลแทน"
  : "เข้าสู่ระบบด้วยลิงก์นี้ไม่ได้ ขอลิงก์ใหม่ได้เลย";

/** set while the browser is away at Google / Apple, so the page it comes back to knows how to word a failure */
const OAUTH_MARK = "memolink-oauth-pending";
const providerName = (p: string) => p === "apple" ? "Apple" : "Google";

/** why a Google / Apple sign-in came back without a session; every case leaves the sign-in page ready for another try */
export function oauthErrorText(provider: string, code: string, desc: string): string {
  const name = providerName(provider);
  if (/access_denied|denied|cancel/i.test(code + " " + desc)) return `ยกเลิกการเข้าสู่ระบบด้วย ${name} แล้ว ลองอีกครั้งหรือใช้อีเมลได้`;
  if (/provider is not enabled|unsupported provider/i.test(desc)) return "ยังเข้าสู่ระบบด้วยบัญชีนี้ไม่ได้ ใช้อีเมลแทนได้";
  if (/code verifier|flow state|both auth code/i.test(desc)) return `เข้าสู่ระบบด้วย ${name} ไม่สำเร็จ กดปุ่ม ${name} อีกครั้งจากหน้านี้`;
  if (/email/i.test(desc)) return `${name} ไม่ได้ส่งอีเมลของบัญชีมา เข้าสู่ระบบด้วยอีเมลแทนได้`;
  return `เข้าสู่ระบบด้วย ${name} ไม่สำเร็จ ลองอีกครั้งหรือใช้อีเมลได้`;
}

/**
 * Restores the saved session (and finishes a magic-link or Google / Apple sign-in if the URL carries one) before
 * anything is shown. Returns the session, or null plus a message when the sign-in in the URL could not be used.
 */
export async function initAuth(cfg: CloudConfig): Promise<{ session: Session | null; linkError: string | null }> {
  const params = new URLSearchParams(location.search);
  const hash = new URLSearchParams(location.hash.replace(/^#/, ""));
  const urlErr = params.get("error_description") || hash.get("error_description") || params.get("error") || hash.get("error");
  const errCode = params.get("error_code") || hash.get("error_code") || params.get("error") || hash.get("error") || "";
  const hadCode = params.has("code");
  let oauth = "";
  try { oauth = sessionStorage.getItem(OAUTH_MARK) ?? ""; sessionStorage.removeItem(OAUTH_MARK); } catch { /* storage blocked */ }
  const errText = (desc: string) => oauth && (urlErr || hadCode) ? oauthErrorText(oauth, errCode, desc) : linkErrorText(desc);
  const c = authClient(cfg);
  const init = await c.auth.initialize();
  let linkError: string | null = urlErr ? errText(urlErr) : null;
  if (!linkError && hadCode && init.error) linkError = errText(init.error.message);
  if (urlErr || hadCode || hash.has("access_token") || hash.has("error")) history.replaceState(null, "", redirectUrl());
  const { data } = await c.auth.getSession();
  // a code with nothing to finish it (its key is gone from this browser) must not leave a silent sign-in page
  if (!data.session && hadCode && !linkError) linkError = errText("code verifier");
  return { session: data.session, linkError: data.session ? null : linkError };
}

export type AuthResult = { ok: true } | { ok: false; message: string };
const authFail = (e: { status?: number; code?: string; message: string }): AuthResult => {
  if (isAuthRetryableFetchError(e)) return { ok: false, message: "เชื่อมต่อไม่ได้ ตรวจอินเทอร์เน็ตแล้วลองอีกครั้ง" };
  if (e.status === 429 || /rate limit|security purposes/i.test(e.message)) return { ok: false, message: "ขอบ่อยเกินไป รอสักครู่แล้วลองอีกครั้ง" };
  if (e.code === "otp_expired" || /expired|invalid/i.test(e.message)) return { ok: false, message: "รหัสไม่ถูกต้องหรือหมดอายุ กรุณาลองใหม่" };
  if (/provider is not enabled|unsupported provider/i.test(e.message)) return { ok: false, message: "ยังเข้าสู่ระบบด้วยบัญชีนี้ไม่ได้ ใช้อีเมลแทนได้" };
  if (/email/i.test(e.message) && e.status === 400) return { ok: false, message: "อีเมลไม่ถูกต้อง" };
  return { ok: false, message: "เข้าสู่ระบบไม่ได้ ลองอีกครั้ง" };
};

export async function sendMagicLink(email: string): Promise<AuthResult> {
  await signingOut; // its clean-up would remove the key this sign-in stores
  try {
    const { error } = await authClient().auth.signInWithOtp({ email, options: { emailRedirectTo: redirectUrl(), shouldCreateUser: true } });
    return error ? authFail(error) : { ok: true };
  } catch { return { ok: false, message: "เชื่อมต่อไม่ได้ ตรวจอินเทอร์เน็ตแล้วลองอีกครั้ง" }; }
}

export async function verifyCode(email: string, code: string): Promise<AuthResult> {
  try {
    const { error } = await authClient().auth.verifyOtp({ email, token: code, type: "email" });
    return error ? authFail(error) : { ok: true };
  } catch { return { ok: false, message: "เชื่อมต่อไม่ได้ ตรวจอินเทอร์เน็ตแล้วลองอีกครั้ง" }; }
}

/**
 * Google / Apple sign-in (Login V2). Sends the browser to the provider; it comes back to this page with a
 * code that initAuth() finishes, like the email link. The provider must be enabled in the Supabase project.
 * An account whose email already signed in with a code is the same account (Supabase links a verified email).
 */
export async function signInWithProvider(provider: "google" | "apple"): Promise<AuthResult> {
  // Google refuses sign-in inside the browsers built into chat and social apps: say so here instead of a Google error page
  if (inAppBrowser()) return { ok: false, message: `เบราว์เซอร์ในแอปนี้เข้าสู่ระบบด้วย ${providerName(provider)} ไม่ได้ เปิด memolink.app ใน Chrome หรือ Safari แล้วลองอีกครั้ง หรือใช้อีเมลแทน` };
  await signingOut;
  try {
    // Supabase answers a provider that is not turned on with a bare JSON error page, so ask first and stay here
    if (!(await providerEnabled(provider))) return authFail({ message: "provider is not enabled" });
    try { sessionStorage.setItem(OAUTH_MARK, provider); } catch { /* storage blocked: a failure is worded like a link error */ }
    const { error } = await authClient().auth.signInWithOAuth({
      provider,
      // always show the account chooser, so after a log out or a failed try another Google account can be picked
      options: { redirectTo: redirectUrl(), queryParams: provider === "google" ? { prompt: "select_account" } : undefined }
    });
    return error ? authFail(error) : { ok: true };
  } catch { return { ok: false, message: "เชื่อมต่อไม่ได้ ตรวจอินเทอร์เน็ตแล้วลองอีกครั้ง" }; }
}

/** LINE, Facebook, Messenger, Instagram and TikTok open links in their own browser, which Google blocks for sign-in */
export const inAppBrowser = (ua = navigator.userAgent) => /\bLine\/|FBAN|FBAV|FB_IAB|Instagram|musical_ly|BytedanceWebview/i.test(ua);

/** how this account has signed in so far, e.g. ["email", "google"] (from the session, no request) */
export function signInMethods(s: Session | null): string[] {
  const p = s?.user.app_metadata?.providers;
  return Array.isArray(p) ? p.map(String) : s?.user.app_metadata?.provider ? [String(s.user.app_metadata.provider)] : [];
}

/** false only when the project's public Auth settings say the provider is off; unknown counts as on */
async function providerEnabled(provider: "google" | "apple"): Promise<boolean> {
  authClient();
  const r = await fetchWithTimeout(`${authCfg!.url}/auth/v1/settings`, { headers: { apikey: authCfg!.anonKey } });
  if (!r.ok) return true;
  const s = await r.json().catch(() => null) as { external?: Record<string, boolean> } | null;
  return s?.external?.[provider] !== false;
}

/** a log out still finishing; a new sign-in waits for it, or the log out would delete that sign-in's PKCE key */
let signingOut: Promise<void> = Promise.resolve();
/** Ends the session on this device only (other devices stay signed in). */
export async function signOutHere() {
  try { localStorage.setItem(LOGOUT_MARK, String(Date.now())); } catch { /* storage blocked */ }
  signingOut = authClient().auth.signOut({ scope: "local" }).then(() => {}, () => { /* offline: the local session is removed anyway */ });
  await signingOut;
}
/** true when a SIGNED_OUT came from someone pressing "log out" (here or in another tab), not from an expired session */
export function wasExplicitLogout(): boolean {
  try { return Date.now() - Number(localStorage.getItem(LOGOUT_MARK) || 0) < 15_000; } catch { return false; }
}

export function onAuthChange(cb: (event: AuthChangeEvent, session: Session | null) => void) {
  // never call back into supabase from inside its own callback
  return authClient().auth.onAuthStateChange((event, session) => { setTimeout(() => cb(event, session), 0); });
}

/**
 * Access token for a request made on behalf of `userId`. Refuses when the device is now signed in as someone else,
 * so a request prepared for one account can never be sent with another account's token.
 */
export async function accessTokenFor(userId: string): Promise<string> {
  let res;
  try { res = await authClient().auth.getSession(); }
  catch { throw new RepoError("offline"); }
  if (res.error && isAuthRetryableFetchError(res.error)) throw new RepoError("offline");
  const s = res.data.session;
  if (!s) throw new RepoError("session");
  if (s.user.id !== userId) throw new RepoError("account");
  return s.access_token;
}
